<template>
<div class="p-3">
    <b-overlay
        :show="$store.getters['loading/isLoading']('maternity')"
        rounded="sm"
        variant="light"
        spinner-variant="danger"
        :opacity="0.80"
        blur="2px">

        <template v-if="!record">
            <b-alert show variant="light" class="border">
                <p class="mb-2"><strong>No maternity record</strong></p>
                <p class="text-muted mb-0">
                    This patient has no active maternity record. Register a pregnancy
                    below to begin antenatal tracking.
                </p>
            </b-alert>
            <maternity-record-form :record="null" />
        </template>

        <template v-else>
            <b-card no-body class="mb-3">
                <b-card-body class="py-2">
                    <b-row class="text-center">
                        <b-col>
                            <div class="small text-muted">Gestational age</div>
                            <div class="h5 mb-0">{{ gestationalAgeDisplay }}</div>
                        </b-col>
                        <b-col>
                            <div class="small text-muted">EDD</div>
                            <div class="h5 mb-0">{{ record.expected_delivery_date ? toLocaleDate(record.expected_delivery_date) : 'n/a' }}</div>
                        </b-col>
                        <b-col>
                            <div class="small text-muted">Obstetric (G/P/A/M)</div>
                            <div class="h5 mb-0">{{ gpa }}</div>
                        </b-col>
                        <b-col>
                            <div class="small text-muted">Trimester</div>
                            <div class="h5 mb-0">{{ trimester }}</div>
                        </b-col>
                    </b-row>
                </b-card-body>
            </b-card>

            <b-tabs pills card content-class="pt-3">
                <b-tab title="Overview" active>
                    <maternity-record-form :record="record" />
                </b-tab>
                <b-tab lazy title="Lab">
                    <referrals-base button-top small :callback="createPatientAdminDiagnostics" />
                </b-tab>
                <b-tab :title="`Pregnancies (${pregnancies.length})`">
                    <pregnancy-history-list :record="record" />
                </b-tab>
                <b-tab :title="`Antenatal Visits (${visits.length})`">
                    <antepartum-visit-list :record="record" />
                </b-tab>
                <b-tab :title="`Echos (${ultrasounds.length})`">
                    <early-ultrasound-list :record="record" />
                </b-tab>
            </b-tabs>
        </template>
    </b-overlay>
</div>
</template>
<script>
import ComponentExtension from "@/core/components/ComponentExtension"
import { mapGetters, mapActions } from "vuex"
import MaternityRecordForm from "./MaternityRecordForm"
import PregnancyHistoryList from "./PregnancyHistoryList"
import AntepartumVisitList from "./AntepartumVisitList"
import EarlyUltrasoundList from "./EarlyUltrasoundList"
import ReferralsBase from "@/core/components/lab/ReferralsBase"

export default ComponentExtension.extend({
    name: "maternity-base",
    components: {
        MaternityRecordForm,
        PregnancyHistoryList,
        AntepartumVisitList,
        EarlyUltrasoundList,
        ReferralsBase,
    },
    computed: {
        ...mapGetters('maternity', ['getMaternityRecord', 'getPregnancies', 'getVisits', 'getUltrasounds']),
        record() {
            return this.getMaternityRecord()
        },
        pregnancies() {
            return this.getPregnancies() || []
        },
        visits() {
            return this.getVisits() || []
        },
        ultrasounds() {
            return this.getUltrasounds() || []
        },
        gestationalAgeDays() {
            if (!this.record || !this.record.expected_delivery_date) {
                return null
            }
            const edd = new Date(this.record.expected_delivery_date)
            const today = new Date()
            const msPerDay = 24 * 60 * 60 * 1000
            const daysToEdd = Math.round((edd - today) / msPerDay)
            return 280 - daysToEdd
        },
        gestationalAgeDisplay() {
            const d = this.gestationalAgeDays
            if (d === null || d < 0 || d > 320) {
                return 'n/a'
            }
            return `${Math.floor(d / 7)}w+${d % 7}d`
        },
        trimester() {
            const d = this.gestationalAgeDays
            if (d === null || d < 0 || d > 320) {
                return 'n/a'
            }
            const w = Math.floor(d / 7)
            if (w < 14) return '1st'
            if (w < 28) return '2nd'
            return '3rd'
        },
        gpa() {
            if (!this.record) {
                return 'n/a'
            }
            const g = this.record.gravida ?? '-'
            const p = this.record.para ?? '-'
            const a = this.record.abortus ?? '-'
            const m = this.record.mater ?? '-'
            return `G${g} P${p} A${a} M${m}`
        },
    },
    methods: {
        ...mapActions('maternity', ['getMaternityRecordForCurrentPatient']),
        ...mapActions('lab', ['createPatientAdminDiagnostics']),
        async loadForPatient(patient) {
            await this.getMaternityRecordForCurrentPatient()
        },
    },
})
</script>
